import { useState, useCallback, useMemo, useRef } from 'react';
import { useMutation, graphql } from 'react-relay';
import { Upload, FileUp, Copy, Check, AlertCircle, UserPlus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogFooter,
} from '@/components/ui/Dialog';
import { ResponsiveDialogContent } from '@/components/ui/ResponsiveDialog';
import {
  ResponsiveDialogHeader,
  ResponsiveDialogHelpButton,
} from '@/components/ui/ResponsiveDialogHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Label } from '@/components/ui/Label';
import { formatDuration } from '@/utils/formatters';
import {
  validateImportFile,
  resolveImportId,
  IMPORT_EXAMPLE_JSON,
  type ImportProjectOption,
  type ImportMemberOption,
  type ImportValidationResult,
} from '@/features/daySheetly/utils/importJson';
import { importTasksMutation } from './ImportJsonDialogMutation';

interface ImportJsonDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projects: ImportProjectOption[];
  members: ImportMemberOption[];
  currentUserId?: string;
  activeWorkspaceId?: string;
  effectivePlan?: 'FREE' | 'PRO';
  fallbackDate: string;
  onImported: () => void;
}

const MAX_FILE_SIZE = 1024 * 1024;
const UNASSIGNED_SENTINEL = '__unassigned__';

type Stage = 'upload' | 'review';

export function ImportJsonDialog({
  isOpen,
  onClose,
  projects,
  members,
  currentUserId,
  activeWorkspaceId,
  effectivePlan = 'FREE',
  fallbackDate,
  onImported,
}: ImportJsonDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>('upload');
  const [fileName, setFileName] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportValidationResult | null>(null);
  const [rawFile, setRawFile] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // rowIndex → workspace member id (or null for unassigned). Only populated
  // for imports that include a members[] block, so the user can re-route
  // unassigned tasks to themselves or another member of the destination
  // workspace.
  const [memberAssignments, setMemberAssignments] = useState<Record<number, string | null>>({});

  const [commitImport] = useMutation(importTasksMutation);

  const reset = useCallback(() => {
    setStage('upload');
    setFileName(null);
    setPreview(null);
    setRawFile(null);
    setIsDragging(false);
    setCopied(false);
    setIsCommitting(false);
    setConfirmOpen(false);
    setMemberAssignments({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const parseFile = useCallback(
    (file: File) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error('File must be smaller than 1 MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        let json: unknown;
        try {
          json = JSON.parse(text);
        } catch {
          setPreview({ rows: [], isValid: false, fileError: 'Invalid JSON file' });
          setRawFile(text);
          setFileName(file.name);
          return;
        }

        const result = validateImportFile(json, {
          projects,
          members,
          currentUserId: currentUserId ?? '',
          fallbackDate,
          activeWorkspaceId,
        });

        setPreview(result);
        setRawFile(text);
        setFileName(file.name);
        setMemberAssignments({});

        // Auto-advance to review once the JSON parses. Row-level validation
        // errors stay visible in the review table with the Import button
        // disabled, so the user can fix the source file (Back) without us
        // having to surface them again here. JSON-level errors (parse
        // failure, missing tasks, version, …) set `fileError` and keep
        // the user on the upload step.
        if (!result.fileError) {
          setStage('review');
        }
      };
      reader.readAsText(file);
    },
    [projects, members, currentUserId, activeWorkspaceId, fallbackDate],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        parseFile(file);
      }
    },
    [parseFile],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragging(false);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        parseFile(file);
      }
    },
    [parseFile],
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  }, []);

  const handleCopyExample = useCallback(() => {
    navigator.clipboard.writeText(IMPORT_EXAMPLE_JSON).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const buildPayloadFile = useCallback((): string => {
    if (!rawFile || !preview) return rawFile ?? '';

    // The import shape is the same regardless of which blocks the user
    // uploaded. The only edit we apply is rewriting `memberId` for rows the
    // user manually reassigned — the server accepts a Relay global id here
    // and resolves it against the active workspace.
    const parsed = JSON.parse(rawFile) as Record<string, unknown>;
    const tasks = Array.isArray(parsed.tasks) ? [...(parsed.tasks as unknown[])] : [];

    const updatedTasks = tasks.map((raw, idx) => {
      const override = memberAssignments[idx];
      if (override === undefined) return raw;
      const task = { ...(raw as Record<string, unknown>) };
      if (override === null) {
        // Drop memberId entirely so the server falls back to current user.
        delete task.memberId;
      } else {
        task.memberId = override;
      }
      return task;
    });

    return JSON.stringify({ ...parsed, tasks: updatedTasks });
  }, [rawFile, preview, memberAssignments]);

  const runImport = useCallback(() => {
    if (!preview?.isValid || (preview.requiresPro && effectivePlan !== 'PRO')) return;

    setIsCommitting(true);
    const file = buildPayloadFile();

    commitImport({
      variables: { file, fallbackDate },
      onError: (err) => {
        setIsCommitting(false);
        if (err.message.toLowerCase().includes('requires pro')) {
          toast.error('This migration requires Pro. Upgrade your plan to continue.');
        } else {
          toast.error(`Failed to import tasks: ${err.message}`);
        }
      },
      onCompleted: (data) => {
        setIsCommitting(false);
        const count = data.importTasks.createdCount;
        toast.success(`Imported ${count} task${count === 1 ? '' : 's'}`);
        onImported();
        handleClose();
      },
    });
  }, [preview?.isValid, preview?.requiresPro, effectivePlan, buildPayloadFile, fallbackDate, commitImport, onImported, handleClose]);

  const handleConfirmImport = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleBackToUpload = useCallback(() => {
    setStage('upload');
  }, []);

  const validRowCount = preview?.rows.filter((r) => r.errors.length === 0).length ?? 0;
  const unassignedIndexes = preview?.unassignedRowIndexes ?? [];

  const projectSummary = useMemo(() => {
    if (!preview?.projectPlan) return null;
    const newCount = preview.projectPlan.filter((p) => !p.existing).length;
    const existingCount = preview.projectPlan.filter((p) => p.existing).length;
    if (newCount === 0 && existingCount === 0) return null;
    return { newCount, existingCount };
  }, [preview?.projectPlan]);

  const memberSummary = useMemo(() => {
    if (!preview?.memberPlan) return null;
    const matched = preview.memberPlan.filter((m) => m.matchedMemberId).length;
    const unmatched = preview.memberPlan.length - matched;
    return { matched, unmatched };
  }, [preview?.memberPlan]);

  const showUpload = isOpen && stage === 'upload';
  const showReview = isOpen && stage === 'review';

  return (
    <>
      {/* ===== Modal 1: Upload ===== */}
      <Dialog
        open={showUpload}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <ResponsiveDialogContent
          data-testid="import-json-upload-dialog"
          className="sm:!max-w-2xl"
        >
          <ResponsiveDialogHeader
            title="Import JSON"
            description="Upload a JSON file to bulk-create tasks. Dates in the file take precedence; missing dates use the currently selected day."
            onBack={handleClose}
            right={<ResponsiveDialogHelpButton label="Import help" />}
          />

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6 sm:pb-4">
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm font-medium">Expected format</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 px-2 text-xs"
                  onClick={handleCopyExample}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <pre className="max-h-32 overflow-auto rounded-md bg-muted p-2 text-xs text-muted-foreground whitespace-pre-wrap break-all">
                {IMPORT_EXAMPLE_JSON}
              </pre>
            </div>

            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-background hover:bg-muted/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                onChange={handleFileChange}
                data-testid="import-json-file-input"
              />
              <FileUp className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">
                {fileName ? fileName : 'Drag & drop a JSON file or click to choose'}
              </p>
              <p className="text-xs text-muted-foreground">
                {fileName ? 'Click to replace' : '.json up to 1 MB'}
              </p>
            </div>

            {preview?.fileError && (
              <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {preview.fileError}
              </div>
            )}
          </div>

        </ResponsiveDialogContent>
      </Dialog>

      {/* ===== Modal 2: Review ===== */}
      <Dialog
        open={showReview}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
      >
        <ResponsiveDialogContent
          data-testid="import-json-review-dialog"
          className="sm:!max-w-2xl"
        >
          <ResponsiveDialogHeader
            title="Import JSON"
            description="Confirm what will be created. Reassign unassigned tasks below before importing."
            onBack={handleBackToUpload}
            backLabel="Back to upload"
            right={<ResponsiveDialogHelpButton label="Import help" />}
          />

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6 sm:pb-4">
            {preview?.requiresPro && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                <p className="font-medium">
                  {preview.importKind === 'AMBIGUOUS'
                    ? 'This legacy file may contain data from another workspace.'
                    : 'This file is a cross-workspace migration.'}
                </p>
                <p className="mt-1 text-muted-foreground">
                  Same-workspace backup and restore are free. Upgrade to Pro to import this file.
                </p>
                {effectivePlan !== 'PRO' && (
                  <a
                    href="/settings/subscription"
                    className="mt-2 inline-block font-medium text-primary underline underline-offset-4"
                  >
                    Upgrade to Pro
                  </a>
                )}
              </div>
            )}
            {(projectSummary || memberSummary) && (
              <div className="rounded-md border bg-muted/20 p-3 text-xs">
                {projectSummary && (
                  <ul className="space-y-1">
                    {projectSummary.newCount > 0 && (
                      <li>
                        <strong>{projectSummary.newCount}</strong>{' '}
                        {projectSummary.newCount === 1 ? 'project' : 'projects'} will be created
                      </li>
                    )}
                    {projectSummary.existingCount > 0 && (
                      <li>
                        <strong>{projectSummary.existingCount}</strong>{' '}
                        {projectSummary.existingCount === 1 ? 'project' : 'projects'} will be reused
                      </li>
                    )}
                  </ul>
                )}
                {memberSummary && (
                  <ul className={projectSummary ? 'mt-2 space-y-1' : 'space-y-1'}>
                    {memberSummary.matched > 0 && (
                      <li>
                        <strong>{memberSummary.matched}</strong>{' '}
                        {memberSummary.matched === 1 ? 'member' : 'members'} will be matched by email
                      </li>
                    )}
                    {memberSummary.unmatched > 0 && (
                      <li>
                        <strong>{memberSummary.unmatched}</strong>{' '}
                        {memberSummary.unmatched === 1 ? 'task will be' : 'tasks will be'} unassigned
                        (member not found). Assign manually below.
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {preview && preview.rows.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-4 gap-2">
                  {[
                    ['Tasks', preview.rows.length],
                    ['Valid', validRowCount],
                    ['Issues', preview.rows.length - validRowCount],
                    ['Projects', projectSummary?.newCount ?? 0],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-md border bg-background p-2 text-center">
                      <p className="text-lg font-semibold text-primary">{value}</p>
                      <p className="text-[10px] text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Preview ({fileName})</Label>
                  <span className="text-xs text-muted-foreground">
                    {validRowCount} of {preview.rows.length} valid
                  </span>
                </div>
                <div className="space-y-2 sm:hidden">
                  {preview.rows.map((row, index) => {
                    const isUnassigned = unassignedIndexes.includes(index);
                    const overrideId = memberAssignments[index];
                    return (
                      <div
                        key={index}
                        data-testid={`import-mobile-row-${index}`}
                        className={`rounded-lg border bg-background p-3 ${
                          row.errors.length > 0 ? 'border-destructive/50 bg-destructive/5' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <span
                            className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs ${
                              row.errors.length > 0
                                ? 'bg-destructive/10 text-destructive'
                                : 'bg-primary/10 text-primary'
                            }`}
                          >
                            {row.errors.length > 0 ? '!' : '✓'}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold">{row.title || 'Untitled task'}</p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {row.date ?? 'No date'} · {row.durationMinutes != null ? formatDuration(row.durationMinutes) : 'No duration'}
                            </p>
                            <p className="mt-1 truncate text-xs text-muted-foreground">
                              {row.projectName ?? 'No project'}
                            </p>
                          </div>
                        </div>
                        {row.errors.length > 0 && (
                          <p className="mt-2 text-xs text-destructive">{row.errors.join(', ')}</p>
                        )}
                        {(isUnassigned || overrideId !== undefined) && (
                          <Select
                            value={
                              overrideId === null || overrideId === undefined
                                ? UNASSIGNED_SENTINEL
                                : overrideId
                            }
                            onValueChange={(value) => {
                              setMemberAssignments((prev) => ({
                                ...prev,
                                [index]: value === UNASSIGNED_SENTINEL ? null : value,
                              }));
                            }}
                          >
                            <SelectTrigger className="mt-3 h-8 w-full text-xs">
                              <SelectValue placeholder="Assign member" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={UNASSIGNED_SENTINEL}>Unassigned</SelectItem>
                              {members.map((m) => (
                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="hidden max-h-72 overflow-auto rounded-md border sm:block">
                  <Table className="min-w-[640px] sm:min-w-full">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Date</TableHead>
                        <TableHead className="whitespace-nowrap">Title</TableHead>
                        <TableHead className="whitespace-nowrap">Duration</TableHead>
                        <TableHead className="whitespace-nowrap">Project</TableHead>
                        <TableHead className="whitespace-nowrap">Member</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.rows.map((row, index) => {
                        const isUnassigned = unassignedIndexes.includes(index);
                        const overrideId = memberAssignments[index];
                        const overriddenMember = members.find(
                          (m) => resolveId(m.id) === (overrideId ?? ''),
                        );
                        return (
                          <TableRow
                            key={index}
                            className={row.errors.length > 0 ? 'bg-destructive/10' : undefined}
                            data-testid={`import-row-${index}`}
                          >
                            <TableCell className="whitespace-nowrap">{row.date ?? '—'}</TableCell>
                            <TableCell>
                              <span className={row.errors.length > 0 ? 'text-destructive' : undefined}>
                                {row.title || '—'}
                              </span>
                              {row.errors.length > 0 && (
                                <span className="ml-2 text-xs text-destructive">
                                  {row.errors.join(', ')}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {row.durationMinutes != null ? formatDuration(row.durationMinutes) : '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {row.projectName ?? '—'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {isUnassigned || overrideId !== undefined ? (
                                <div className="flex items-center gap-2">
                                  <Select
                                    value={
                                      overrideId === null || overrideId === undefined
                                        ? UNASSIGNED_SENTINEL
                                        : overrideId
                                    }
                                    onValueChange={(value) => {
                                      setMemberAssignments((prev) => ({
                                        ...prev,
                                        [index]: value === UNASSIGNED_SENTINEL ? null : value,
                                      }));
                                    }}
                                  >
                                    <SelectTrigger className="h-7 w-[180px] text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={UNASSIGNED_SENTINEL}>
                                        <span className="flex items-center gap-1 text-muted-foreground">
                                          <UserPlus className="h-3 w-3" /> Unassigned
                                        </span>
                                      </SelectItem>
                                      {members.map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                          {m.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              ) : (
                                row.memberName ?? '—'
                              )}
                              {isUnassigned && overrideId === undefined && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (unmatched)
                                </span>
                              )}
                              {overriddenMember && (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  (→ {overriddenMember.name})
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter
            data-testid="import-json-actions"
            className="!flex-row gap-3 border-t bg-background px-4 py-4 sm:px-6 [&>button]:min-w-0 [&>button]:flex-1"
          >
            <Button
              type="button"
              variant="outline"
              onClick={handleBackToUpload}
              disabled={isCommitting}
              className="flex-1"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button
              type="button"
              onClick={handleConfirmImport}
              disabled={!preview?.isValid || (preview?.requiresPro && effectivePlan !== 'PRO') || isCommitting}
              data-testid="import-json-submit"
              className="flex-1"
            >
              <Upload className="mr-2 h-4 w-4" />
              Import {validRowCount > 0 ? validRowCount : ''} task{validRowCount === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* ===== Confirmation dialog ===== */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Import {validRowCount} task{validRowCount === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will create the {validRowCount} task
              {validRowCount === 1 ? '' : 's'} listed above on the dates specified in the file.
              {projectSummary && projectSummary.newCount > 0 && (
                <>
                  {' '}
                  {projectSummary.newCount} project
                  {projectSummary.newCount === 1 ? '' : 's'} will be created in this workspace.
                </>
              )}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCommitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runImport}
              disabled={isCommitting}
              data-testid="import-json-confirm"
            >
              {isCommitting ? 'Importing…' : 'Import'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Mirror the resolver used by the validator so we can compare override ids.
function resolveId(input: string): string {
  return resolveImportId(input);
}
